import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPlus, FaEye, FaEdit, FaToggleOn, FaToggleOff, FaTrash, FaFilePdf, FaPrint } from 'react-icons/fa';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  getProjects,
  getProjectDetails,
  deleteProject,
  toggleProjectStatus,
  getCustomers,
  clearSuccess,
  clearError,
} from '../../../../redux/Slices/authSlice';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import { getAuthUserData, hasAnyPermission } from '../../../../shared/utils.js/permissionUtils';
import PdfPreviewModal from '../../../../shared/components/PdfPreviewModal';

const COLORS = {
  primary: '#11324D',
  accent: '#AC2000',
  secondary: '#F6630D',
  neutral: '#B3B3B3',
  light: '#F5F5DC',
};

const STATUS_OPTIONS = [
  { value: '', label: 'كل الحالات' },
  { value: 'planned', label: 'مخطط' },
  { value: 'in_progress', label: 'قيد التنفيذ' },
  { value: 'paused', label: 'متوقف' },
  { value: 'completed', label: 'مكتمل' },
  { value: 'cancelled', label: 'ملغى' },
];

const STATUS_META = {
  planned: { label: 'مخطط', color: 'white', bg: 'rgba(17,50,77,0.6)' },
  in_progress: { label: 'قيد التنفيذ', color: COLORS.secondary, bg: 'rgba(246,99,13,0.3)' },
  paused: { label: 'متوقف', color: '#f1c40f', bg: 'rgba(241,196,15,0.3)' },
  completed: { label: 'مكتمل', color: '#27ae60', bg: 'rgba(39,174,96,0.3)' },
  cancelled: { label: 'ملغى', color: '#c0392b', bg: 'rgba(192,57,43,0.3)' },
};

// Projects component
const Projects = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const { projects, projectsPagination, customers, customersPagination, user } = useSelector((state) => state.auth);
  const canCreateProject = hasAnyPermission(getAuthUserData(user), 'projects.create');

  const [currentPage, setCurrentPage] = useState(1);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [filters, setFilters] = useState({ status: '', customerId: null, customerName: '' });

  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerPage, setCustomerPage] = useState(1);
  const customerSearchRef = useRef(null);
  const customerSearchTimeout = useRef(null);

  const [projectToDelete, setProjectToDelete] = useState(null);

  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [projectForPrint, setProjectForPrint] = useState(null);
  const [fullProjectsMap, setFullProjectsMap] = useState({});
  const publicUrl = (
    process.env.PUBLIC_URL ||
    (typeof window !== 'undefined' ? window.location.origin : '')
  ).replace(/\/$/, '');
  const headerLogo = `${publicUrl}/ACE.png`;

  useEffect(() => {
    setTitle('إدارة المشاريع');
    
    dispatch(clearSuccess());
    dispatch(clearError());
  }, [setTitle, dispatch]);

  useEffect(() => {
    const fetchProjects = async () => {
      setIsTableLoading(true);
      const params = {
        page: currentPage,
        per_page: 10,
      };
      if (filters.status) params.status = filters.status;
      if (filters.customerId) params.customer_id = filters.customerId;

      try {
        await dispatch(getProjects(params)).unwrap();
      } catch (error) {
        toast.error(error || 'حدث خطأ أثناء جلب المشاريع');
      } finally {
        setIsTableLoading(false);
      }
    };

    fetchProjects();
  }, [dispatch, currentPage, filters]);

  useEffect(() => {
    if (!customerDropdownOpen) return;

    const loadCustomers = async () => {
      try {
        await dispatch(
          getCustomers({
            page: customerPage,
            per_page: 10,
            search: customerSearchTerm || undefined,
          })
        ).unwrap();
      } catch {
        // errors handled globally
      }
    };

    loadCustomers();
  }, [customerDropdownOpen, customerPage, customerSearchTerm, dispatch]);

  useEffect(() => {
    if (customerDropdownOpen && customerSearchRef.current) {
      customerSearchRef.current.focus();
    }
  }, [customerDropdownOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (customerDropdownOpen && !event.target.closest('[data-customer-dropdown]')) {
        setCustomerDropdownOpen(false);
        setCustomerSearchTerm('');
        setCustomerPage(1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [customerDropdownOpen]);

  const handleCustomerSearch = (value) => {
    setCustomerSearchTerm(value);
    if (customerSearchTimeout.current) {
      clearTimeout(customerSearchTimeout.current);
    }
    customerSearchTimeout.current = setTimeout(() => {
      setCustomerPage(1);
    }, 250);
  };

  // Toggle project active status
  const handleToggleStatus = async (project) => {
    try {
      await dispatch(toggleProjectStatus(project.id)).unwrap();
      toast.success('تم تحديث حالة المشروع');
      dispatch(clearSuccess());
      dispatch(
        getProjects({
          page: currentPage,
          per_page: 10,
          status: filters.status || undefined,
          customer_id: filters.customerId || undefined,
        })
      );
    } catch (error) {
      toast.error(error || 'تعذر تحديث حالة المشروع');
      dispatch(clearError());
    }
  };

  const handleDelete = async () => {
    if (!projectToDelete) return;
    try {
      await dispatch(deleteProject(projectToDelete.id)).unwrap();
      toast.success('تم حذف المشروع بنجاح');
      setProjectToDelete(null);
      dispatch(clearSuccess()); // Clear success message after delete
      dispatch(
        getProjects({
          page: currentPage,
          per_page: 10,
          status: filters.status || undefined,
          customer_id: filters.customerId || undefined,
        })
      );
    } catch (error) {
      toast.error(error || 'تعذر حذف المشروع');
      dispatch(clearError());
    }
  };

  const resetFilters = () => {
    setFilters({ status: '', customerId: null, customerName: '' });
    setCustomerSearchTerm('');
    setCustomerPage(1);
    setCurrentPage(1);
  };

  const fetchFullProject = async (id, forceRefresh = false) => {
    if (!forceRefresh && fullProjectsMap[id]) return fullProjectsMap[id];
    try {
      const response = await dispatch(getProjectDetails(id)).unwrap();
      const details = response?.data || response;
      setFullProjectsMap((prev) => ({ ...prev, [id]: details }));
      return details;
    } catch (err) {
      return null;
    }
  };

  const saveCanvasAsMultiPagePdf = (canvas, fileName) => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;
    pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, imgHeight);
    heightLeft -= pdfHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, imgHeight);
      heightLeft -= pdfHeight;
    }

    pdf.save(fileName);
  };

  const handlePreviewProject = async (project) => {
    setIsGeneratingPdf(true);
    try {
      const details = await fetchFullProject(project.id);
      setProjectForPrint(details || project);
      setIsPreviewOpen(true);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handlePrintProject = async (project) => {
    setIsGeneratingPdf(true);
    try {
      const details = project?.id ? await fetchFullProject(project.id) : null;
      const toPrint = details || project;
      setProjectForPrint(toPrint);

      await new Promise((r) => setTimeout(r, 500));
      const element = document.getElementById(`project-pdf-${toPrint.id}`);
      if (!element) throw new Error('PDF template not found');

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      saveCanvasAsMultiPagePdf(canvas, `Project_${toPrint.code || toPrint.id}.pdf`);
      toast.success('تم تحميل الملف بنجاح');
    } catch (err) {
      console.error(err);
      toast.error('حدث خطأ أثناء الطباعة');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handlePrintAll = async () => {
    setIsGeneratingPdf(true);
    try {
      await new Promise((r) => setTimeout(r, 500));
      const element = document.getElementById('all-projects-pdf');
      if (!element) throw new Error('PDF template not found');

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(
        canvas.toDataURL('image/jpeg', 0.95),
        'JPEG',
        0,
        0,
        pdfWidth,
        Math.min(pdfHeight, pdf.internal.pageSize.getHeight())
      );
      pdf.save(`Projects_List_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('تم تحميل الملف بنجاح');
    } catch (err) {
      console.error(err);
      toast.error('حدث خطأ أثناء الطباعة');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const renderCustomerName = (project) =>
    project?.customer?.name_ar || project?.customer?.name || project.customer_name || '—';

  const renderStatus = (value) => {
    const meta = STATUS_META[value] || { label: value || 'غير محدد', color: COLORS.neutral, bg: 'rgba(179,179,179,0.2)' };
    return (
      <span
        style={{
          backgroundColor: meta.bg,
          color: meta.color,
          padding: '6px 16px',
          borderRadius: 18,
          fontSize: 12,
          fontWeight: 600,
          display: 'inline-flex',
          minWidth: 110,
          justifyContent: 'center',
          whiteSpace: 'nowrap',
        }}
      >
        {meta.label}
      </span>
    );
  };

  const formatDate = (date) => (date ? String(date).split('T')[0] : '—');

  const formatCurrency = (value) =>
    value === undefined || value === null
      ? '—'
      : Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Style definitions
  const labelStyle = { display: 'block', marginBottom: 6, fontSize: 14, color: 'white' };
  const selectStyle = {
    width: '100%',
    padding: 10,
    backgroundColor: '#1a1f2e',
    border: '1px solid #333',
    borderRadius: 8,
    color: 'white',
    fontSize: 14,
  };
  const dropdownToggleStyle = {
    width: '100%',
    padding: 10,
    backgroundColor: '#1a1f2e',
    border: '1px solid #333',
    borderRadius: 8,
    color: 'white',
    fontSize: 14,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
  };
  const dropdownMenuStyle = {
    position: 'absolute',
    zIndex: 10,
    top: '100%',
    left: 0,
    right: 0,
    background: '#1a1f2e',
    border: '1px solid #333',
    borderRadius: 8,
    marginTop: 4,
    maxHeight: 400,
    overflowY: 'auto',
  };
  const inputStyle = {
    width: '100%',
    padding: 8,
    backgroundColor: '#14263b',
    border: '1px solid #444',
    borderRadius: 6,
    color: 'white',
    fontSize: 14,
  };
  const dropdownPaginationStyle = {
    padding: '8px',
    borderTop: '1px solid #333',
    background: '#14263b',
  };
  const dropdownPaginationInfo = {
    color: '#ccc',
    fontSize: 11,
    marginBottom: 6,
    textAlign: 'center',
  };
  const dropdownPaginationControls = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    flexWrap: 'wrap',
  };
  const dropdownItemStyle = {
    padding: 10,
    cursor: 'pointer',
    borderTop: '1px solid #333',
    color: 'white',
  };
  const actionButton = (bg, color = '#fff') => ({
    backgroundColor: bg,
    color: color,
    border: 'none',
    borderRadius: 6,
    padding: '6px 12px',
    cursor: 'pointer',
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  });
  const headerStyle = {
    padding: 12,
    textAlign: 'center',
    color: 'white',
    fontWeight: 600,
    fontSize: 14,
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  };
  const cellStyle = {
    padding: 12,
    color: 'white',
    fontSize: 14,
    borderBottom: '1px solid #2c4a70',
    textAlign: 'center',
  };
  const paginationButton = (disabled) => ({
    padding: '8px 12px',
    backgroundColor: disabled ? '#333' : COLORS.accent,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: disabled ? 'not-allowed' : 'pointer',
  });

  const pageButton = (active) => ({
    padding: '8px 12px',
    backgroundColor: active ? COLORS.accent : '#1a1f2e',
    color: 'white',
    border: '1px solid #333',
    borderRadius: '4px',
    cursor: 'pointer',
  });
  const modalOverlay = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };
  const modalCard = {
    background: '#1b3350',
    borderRadius: 12,
    padding: 24,
    minWidth: 400,
    maxWidth: 500,
    border: '1px solid #333',
  };

  return (
    <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ margin: 0, fontSize: 20 }}>إدارة المشاريع</h1>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button
            onClick={handlePrintAll}
            disabled={isGeneratingPdf}
            style={{
              backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              opacity: isGeneratingPdf ? 0.6 : 1,
            }}
          >
            <FaPrint /> {isGeneratingPdf ? 'جاري الطباعة...' : 'طباعة الكل'}
          </button>
          {canCreateProject && (
            <button
              onClick={() => navigate('/projects/add')}
              style={{
                backgroundColor: COLORS.accent,
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '10px 16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <FaPlus /> مشروع جديد
            </button>
          )}
        </div>
      </div>

      <div style={{ background: '#1c2533', borderRadius: 12, padding: 16, marginBottom: 20, border: '1px solid #2c4a70' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 220px' }}>
            <label style={labelStyle}>الحالة</label>
            <select
              value={filters.status}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, status: e.target.value }));
                setCurrentPage(1);
              }}
              style={selectStyle}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ flex: '1 1 260px', position: 'relative' }} data-customer-dropdown>
            <label style={labelStyle}>العميل</label>
            <div
              onClick={() => {
                const next = !customerDropdownOpen;
                setCustomerDropdownOpen(next);
                if (!next) {
                  setCustomerSearchTerm('');
                  setCustomerPage(1);
                }
              }}
              style={dropdownToggleStyle}
            >
              <span>{filters.customerName || 'اختر العميل'}</span>
              <span style={{ fontSize: 12 }}>▼</span>
            </div>
            {customerDropdownOpen && (
              <div style={dropdownMenuStyle} onClick={(e) => e.stopPropagation()}>
                <div style={{ padding: 8 }}>
                  <input
                    ref={customerSearchRef}
                    type="text"
                    placeholder="ابحث عن عميل"
                    value={customerSearchTerm}
                    onChange={(e) => handleCustomerSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    style={inputStyle}
                  />
                </div>
                {customersPagination && customersPagination.last_page >= 1 && (
                  <div style={dropdownPaginationStyle}>
                    <div style={dropdownPaginationInfo}>
                      الصفحة {customersPagination.current_page || 1} من {customersPagination.last_page || 1}
                    </div>
                    <div style={dropdownPaginationControls}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if ((customersPagination.current_page || 1) > 1) {
                            setCustomerPage(1);
                          }
                        }}
                        disabled={(customersPagination.current_page || 1) === 1}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: (customersPagination.current_page || 1) === 1 ? '#2a2a2a' : '#1a1f2e',
                          color: (customersPagination.current_page || 1) === 1 ? '#666' : 'white',
                          border: '1px solid #444',
                          borderRadius: '4px',
                          cursor: (customersPagination.current_page || 1) === 1 ? 'not-allowed' : 'pointer',
                          fontSize: 10,
                          fontWeight: 'bold',
                          minWidth: '28px',
                        }}
                        title="الصفحة الأولى"
                      >
                        ««
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if ((customersPagination.current_page || 1) > 1) {
                            setCustomerPage((prev) => Math.max(1, prev - 1));
                          }
                        }}
                        disabled={(customersPagination.current_page || 1) === 1}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: (customersPagination.current_page || 1) === 1 ? '#2a2a2a' : '#1a1f2e',
                          color: (customersPagination.current_page || 1) === 1 ? '#666' : 'white',
                          border: '1px solid #444',
                          borderRadius: '4px',
                          cursor: (customersPagination.current_page || 1) === 1 ? 'not-allowed' : 'pointer',
                          fontSize: 10,
                          fontWeight: 'bold',
                          minWidth: '28px',
                        }}
                        title="السابق"
                      >
                        ‹
                      </button>
                      {(() => {
                        const pages = [];
                        const current = customersPagination.current_page || 1;
                        const last = customersPagination.last_page || 1;
                        const showPages = 5;
                        let start = Math.max(1, current - Math.floor(showPages / 2));
                        let end = Math.min(last, start + showPages - 1);
                        if (end - start < showPages - 1) {
                          start = Math.max(1, end - showPages + 1);
                        }
                        for (let i = start; i <= end; i++) {
                          pages.push(i);
                        }
                        return pages.map((page) => (
                          <button
                            key={page}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCustomerPage(page);
                            }}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: page === current ? '#AC2000' : '#1a1f2e',
                              color: 'white',
                              border: page === current ? '1px solid #AC2000' : '1px solid #444',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: 10,
                              fontWeight: page === current ? 'bold' : 'normal',
                              minWidth: '28px',
                            }}
                          >
                            {page}
                          </button>
                        ));
                      })()}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if ((customersPagination.current_page || 1) < (customersPagination.last_page || 1)) {
                            setCustomerPage((prev) => prev + 1);
                          }
                        }}
                        disabled={(customersPagination.current_page || 1) === (customersPagination.last_page || 1)}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: (customersPagination.current_page || 1) === (customersPagination.last_page || 1) ? '#2a2a2a' : '#1a1f2e',
                          color: (customersPagination.current_page || 1) === (customersPagination.last_page || 1) ? '#666' : 'white',
                          border: '1px solid #444',
                          borderRadius: '4px',
                          cursor: (customersPagination.current_page || 1) === (customersPagination.last_page || 1) ? 'not-allowed' : 'pointer',
                          fontSize: 10,
                          fontWeight: 'bold',
                          minWidth: '28px',
                        }}
                        title="التالي"
                      >
                        ›
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const last = customersPagination.last_page || 1;
                          if ((customersPagination.current_page || 1) < last) {
                            setCustomerPage(last);
                          }
                        }}
                        disabled={(customersPagination.current_page || 1) === (customersPagination.last_page || 1)}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: (customersPagination.current_page || 1) === (customersPagination.last_page || 1) ? '#2a2a2a' : '#1a1f2e',
                          color: (customersPagination.current_page || 1) === (customersPagination.last_page || 1) ? '#666' : 'white',
                          border: '1px solid #444',
                          borderRadius: '4px',
                          cursor: (customersPagination.current_page || 1) === (customersPagination.last_page || 1) ? 'not-allowed' : 'pointer',
                          fontSize: 10,
                          fontWeight: 'bold',
                          minWidth: '28px',
                        }}
                        title="الصفحة الأخيرة"
                      >
                        »»
                      </button>
                    </div>
                  </div>
                )}
                <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                  {(customers || []).map((customer) => (
                    <div
                      key={customer.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        setFilters((prev) => ({
                          ...prev,
                          customerId: customer.id,
                          customerName: customer.name_ar || customer.name || customer.code || 'عميل',
                        }));
                        setCustomerDropdownOpen(false);
                        setCustomerSearchTerm('');
                        setCustomerPage(1);
                        setCurrentPage(1);
                      }}
                      style={dropdownItemStyle}
                    >
                      {customer.code ? `${customer.code} - ` : ''}
                      {customer.name_ar || customer.name || 'بدون اسم'}
                    </div>
                  ))}
                  {(customers || []).length === 0 && (
                    <div style={{ padding: 12, textAlign: 'center', color: COLORS.neutral }}>لا يوجد نتائج</div>
                  )}
                </div>
                {filters.customerId && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      resetFilters();
                    }}
                    style={{ ...actionButton('#2f3644'), width: '100%', marginTop: 10 }}
                  >
                    إزالة التحديد
                  </button>
                )}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <button onClick={() => setCurrentPage(1)} style={actionButton(COLORS.primary)}>
              تطبيق
            </button>
            <button onClick={resetFilters} style={actionButton('transparent', '#fff')}>
              إعادة التعيين
            </button>
          </div>
        </div>
      </div>

      <div style={{ background: '#1b3350', borderRadius: 12, border: '1px solid #2c4a70', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: 1024, borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr style={{ backgroundColor: COLORS.accent }}>
                <th style={headerStyle}>الكود</th>
                <th style={headerStyle}>اسم المشروع (عربي)</th>
                <th style={headerStyle}>اسم المشروع (إنجليزي)</th>
                <th style={headerStyle}>العميل</th>
                <th style={headerStyle}>تاريخ البداية</th>
                <th style={headerStyle}>تاريخ النهاية</th>
                <th style={headerStyle}>الحالة</th>
                <th style={headerStyle}>قيمة العقد</th>
                <th style={headerStyle}>تفعيل الحساب</th>
                <th style={headerStyle}>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {(!projects || projects.length === 0) && !isTableLoading ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: 20, color: COLORS.neutral }}>
                    لا توجد بيانات لعرضها
                  </td>
                </tr>
              ) : (
                (projects || []).map((project) => (
                  <tr key={project.id} style={{ borderBottom: '1px solid #2c4a70' }}>
                    <td style={{ ...cellStyle, whiteSpace: 'nowrap', minWidth: 120 }}>{project.code || '—'}</td>
                    <td style={{ ...cellStyle, whiteSpace: 'nowrap', minWidth: 200 }}>{project.name_ar || '—'}</td>
                    <td style={{ ...cellStyle, whiteSpace: 'nowrap', minWidth: 200 }}>{project.name_en || '—'}</td>
                    <td style={{ ...cellStyle, whiteSpace: 'nowrap', minWidth: 180 }}>{renderCustomerName(project)}</td>
                    <td style={{ ...cellStyle, whiteSpace: 'nowrap', minWidth: 120 }}>{formatDate(project.start_date)}</td>
                    <td style={{ ...cellStyle, whiteSpace: 'nowrap', minWidth: 120 }}>{formatDate(project.end_date)}</td>
                    <td style={{ ...cellStyle, whiteSpace: 'nowrap', minWidth: 120, display: 'flex', justifyContent: 'center' }}>{renderStatus(project.status)}</td>
                    <td style={{ ...cellStyle, whiteSpace: 'nowrap', minWidth: 120 }}>{formatCurrency(project.contract_value)}</td>
                    <td style={{ ...cellStyle, whiteSpace: 'nowrap', minWidth: 120, display: 'flex', justifyContent: 'center' }}>
                      <span
                        style={{
                          backgroundColor: project.is_active ? COLORS.primary : '#3a3f50',
                          color: 'white',
                          padding: '6px 16px',
                          borderRadius: 18,
                          fontSize: 12,
                          fontWeight: 'bold',
                          display: 'inline-flex',
                          minWidth: 100,
                          justifyContent: 'center',
                          alignItems: 'center',
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {project.is_active ? 'مفعل' : 'غير مفعل'}
                      </span>
                    </td>
                    <td style={{ ...cellStyle, textAlign: 'center', whiteSpace: 'nowrap', minWidth: '200px' }}>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                        <ActionButton title="عرض" onClick={() => navigate(`/projects/view/${project.id}`)}>
                          <FaEye />
                        </ActionButton>
                        <ActionButton title="تعديل" onClick={() => navigate(`/projects/edit/${project.id}`)}>
                          <FaEdit />
                        </ActionButton>
                        <ActionButton
                          title="طباعة PDF"
                          bg={isGeneratingPdf ? '#666' : '#17a2b8'}
                          onClick={() => handlePreviewProject(project)}
                        >
                          <FaFilePdf />
                        </ActionButton>
                        <ActionButton title="حذف" bg="#c0392b" onClick={() => setProjectToDelete(project)}>
                          <FaTrash />
                        </ActionButton>
                        <ActionButton title="تغيير الحالة" bg="#34495e" onClick={() => handleToggleStatus(project)}>
                          {project.is_active ? <FaToggleOn /> : <FaToggleOff />}
                        </ActionButton>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {projectsPagination && (() => {
          const current_page = projectsPagination.current_page || 1;
          const last_page = projectsPagination.last_page || 1;
          const pages = [];
          for (let i = 1; i <= last_page; i++) {
            if (i === 1 || i === last_page || (i >= current_page - 2 && i <= current_page + 2)) {
              pages.push(i);
            } else if (i === current_page - 3 || i === current_page + 3) {
              pages.push('...');
            }
          }
          return (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px', padding: '16px', borderTop: '1px solid #2c4a70' }}>
              <button
                onClick={() => setCurrentPage(current_page - 1)}
                disabled={current_page === 1}
                style={paginationButton(current_page === 1)}
              >
                السابق
              </button>
              {pages.map((page, idx) =>
                page === '...' ? (
                  <span key={`ellipsis-${idx}`} style={{ padding: '8px', color: 'white' }}>...</span>
                ) : (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    style={pageButton(page === current_page)}
                  >
                    {page}
                  </button>
                )
              )}
              <button
                onClick={() => setCurrentPage(current_page + 1)}
                disabled={current_page === last_page}
                style={paginationButton(current_page === last_page)}
              >
                التالي
              </button>
            </div>
          );
        })()}
      </div>

      {projectToDelete && (
        <div style={modalOverlay} onClick={() => setProjectToDelete(null)}>
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: 12 }}>حذف المشروع</h3>
            <p style={{ color: COLORS.neutral, marginBottom: 8 }}>هل أنت متأكد من حذف هذا المشروع؟</p>
            <p style={{ fontWeight: 600, marginBottom: 18 }}>{projectToDelete.name_ar || projectToDelete.name_en || projectToDelete.code}</p>
            <p style={{ color: '#c0392b', fontSize: 13, marginBottom: 20 }}>لا يمكن التراجع عن هذه العملية</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setProjectToDelete(null)} style={actionButton('transparent', '#fff')}>
                إلغاء
              </button>
              <button onClick={handleDelete} style={actionButton('#c0392b')}>
                تأكيد الحذف
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden PDF templates for capture */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        <AllProjectsPrintTemplate
          projects={projects || []}
          publicUrl={publicUrl}
          headerLogo={headerLogo}
          renderCustomerNameText={renderCustomerName}
          formatDate={formatDate}
          formatCurrency={formatCurrency}
        />

        {projectForPrint && (
          <SingleProjectPrintTemplate
            key={projectForPrint.id}
            project={projectForPrint}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
            formatDate={formatDate}
            formatCurrency={formatCurrency}
          />
        )}
      </div>

      <PdfPreviewModal
        isOpen={isPreviewOpen && !!projectForPrint?.id}
        title="معاينة المشروع"
        onClose={() => {
          setIsPreviewOpen(false);
          setProjectForPrint(null);
        }}
        onPrint={() => handlePrintProject(projectForPrint)}
        isGeneratingPdf={isGeneratingPdf}
      >
        <SingleProjectPrintTemplate
          project={projectForPrint}
          publicUrl={publicUrl}
          headerLogo={headerLogo}
          formatDate={formatDate}
          formatCurrency={formatCurrency}
          isPreview
        />
      </PdfPreviewModal>
    </div>
  );
};

const ActionButton = ({ children, onClick, title, bg = '#2f3644' }) => (
  <button
    onClick={onClick}
    title={title}
    style={{
      backgroundColor: bg,
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      padding: '8px 10px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '38px',
    }}
  >
    {children}
  </button>
);

// --- Styles for Print Templates ---
const reportLabelStyle = {
  border: '1px solid #000',
  padding: '8px 10px',
  fontWeight: 'bold',
  backgroundColor: '#f9f9f9',
  width: '160px',
  textAlign: 'right',
};

const reportValueStyle = {
  border: '1px solid #000',
  padding: '8px 10px',
  textAlign: 'right',
};

const tableHeaderStyle = {
  border: '1px solid #000',
  padding: '8px 4px',
  fontWeight: 'bold',
};

const tableCellStyle = {
  border: '1px solid #000',
  padding: '6px 4px',
};

// --- COMPONENT: Single Project Print Template ---
const SingleProjectPrintTemplate = ({
  project,
  publicUrl,
  headerLogo,
  formatDate,
  formatCurrency,
  isPreview = false,
}) => {
  if (!project) return null;
  const statusMeta = STATUS_META[project.status] || { label: project.status || '—' };
  const customerName = project?.customer?.name_ar || project?.customer?.name || project.customer_name || '—';

  const rows = [
    ['كود المشروع', project.code || '—'],
    ['اسم المشروع (عربي)', project.name_ar || '—'],
    ['اسم المشروع (إنجليزي)', project.name_en || '—'],
    ['العميل', customerName],
    ['تاريخ البداية', formatDate(project.start_date)],
    ['تاريخ النهاية', formatDate(project.end_date)],
    ['الحالة', statusMeta.label],
    ['تفعيل الحساب', project.is_active ? 'مفعل' : 'غير مفعل'],
    ['قيمة العقد', formatCurrency(project.contract_value)],
    ['الضريبة', project.tax?.name_ar || project.tax?.name_en || '—'],
    ['حساب الإيرادات', project.revenue_account ? `${project.revenue_account.code || ''} - ${project.revenue_account.name_ar || project.revenue_account.name || ''}` : '—'],
    ['حساب قيد التنفيذ', project.wip_account ? `${project.wip_account.code || ''} - ${project.wip_account.name_ar || project.wip_account.name || ''}` : '—'],
    ['مركز التكلفة', project.cost_center ? `${project.cost_center.code || ''} - ${project.cost_center.name_ar || project.cost_center.name || ''}` : '—'],
  ];

  return (
    <div
      id={isPreview ? `project-preview-${project.id}` : `project-pdf-${project.id}`}
      style={{
        width: '794px',
        minHeight: '1123px',
        padding: '30px',
        boxSizing: 'border-box',
        backgroundColor: '#fff',
        color: '#000',
        fontFamily: '"Times New Roman Bold", "Times New Roman", serif',
        position: isPreview ? 'relative' : 'absolute',
        left: isPreview ? '0' : '-9999px',
        margin: isPreview ? '0 auto' : '0',
      }}
    >
      <div style={{ border: '5px solid #000', padding: '3px', minHeight: '1053px' }}>
        <div
          style={{
            border: '2px solid #000',
            minHeight: '1043px',
            padding: '15px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: `url(${headerLogo})`,
              backgroundSize: '80%',
              backgroundPosition: 'center center',
              backgroundRepeat: 'no-repeat',
              opacity: 0.08,
              zIndex: 0,
              pointerEvents: 'none',
            }}
          />

          <img
            src={`${publicUrl}/HeaderAce.png`}
            alt="Header"
            style={{ width: '100%', height: 'auto', borderBottom: '4px double #000', paddingBottom: '10px' }}
            crossOrigin="anonymous"
          />

          <div style={{ flex: 1, padding: '10px 0', zIndex: 1, direction: 'rtl', textAlign: 'right' }}>
            <h1 style={{ textAlign: 'center', fontSize: '18px', marginBottom: '4px', fontWeight: 'bold', color: '#000080' }}>
              بيان مشروع
            </h1>
            <p style={{ textAlign: 'center', fontSize: '12px', margin: '0 0 12px', color: '#333', fontFamily: '"Times New Roman", serif' }}>
              {new Date().toLocaleDateString('ar-SA', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '20px' }}>
              <tbody>
                {rows.map(([label, value]) => (
                  <tr key={label}>
                    <td style={reportLabelStyle}>{label}</td>
                    <td style={reportValueStyle}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ fontSize: '13px' }}>
              <div style={{ ...reportLabelStyle, width: '100%', marginBottom: '4px', boxSizing: 'border-box' }}>ملاحظات</div>
              <div style={{ ...reportValueStyle, minHeight: '60px', boxSizing: 'border-box' }}>{project.notes || '—'}</div>
            </div>
          </div>

          <img
            src={`${publicUrl}/FooterAce.png`}
            alt="Footer"
            style={{ width: '100%', height: 'auto', marginTop: 'auto', borderTop: '2px solid #000', paddingTop: '5px' }}
            crossOrigin="anonymous"
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '7px', marginTop: '3px', position: 'relative', zIndex: 1 }}>
            <span>مكتب البرقاوي مهندسون استشاريون</span>
            <span>{new Date().toLocaleDateString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- COMPONENT: All Projects List Print Template ---
const AllProjectsPrintTemplate = ({
  projects,
  publicUrl,
  headerLogo,
  renderCustomerNameText,
  formatDate,
  formatCurrency,
  isPreview = false,
}) => {
  return (
    <div
      id={isPreview ? 'all-projects-preview' : 'all-projects-pdf'}
      style={{
        width: '794px',
        minHeight: '1123px',
        padding: '30px',
        boxSizing: 'border-box',
        backgroundColor: '#fff',
        color: '#000',
        fontFamily: '"Times New Roman Bold", "Times New Roman", serif',
        position: isPreview ? 'relative' : 'absolute',
        left: isPreview ? '0' : '-9999px',
        margin: isPreview ? '0 auto' : '0',
      }}
    >
      <div style={{ border: '5px solid #000', padding: '3px', minHeight: '1053px' }}>
        <div
          style={{
            border: '2px solid #000',
            minHeight: '1043px',
            padding: '15px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: `url(${headerLogo})`,
              backgroundSize: '80%',
              backgroundPosition: 'center center',
              backgroundRepeat: 'no-repeat',
              opacity: 0.08,
              zIndex: 0,
              pointerEvents: 'none',
            }}
          />

          <img
            src={`${publicUrl}/HeaderAce.png`}
            alt="Header"
            style={{ width: '100%', height: 'auto', borderBottom: '4px double #000', paddingBottom: '10px' }}
            crossOrigin="anonymous"
          />

          <div style={{ flex: 1, padding: '10px 0', zIndex: 1, direction: 'rtl', textAlign: 'right' }}>
            <h1 style={{ textAlign: 'center', fontSize: '18px', marginBottom: '4px', fontWeight: 'bold', color: '#000080' }}>
              كشف المشاريع
            </h1>
            <p style={{ textAlign: 'center', fontSize: '12px', margin: '0 0 8px', color: '#333', fontFamily: '"Times New Roman", serif' }}>
              {new Date().toLocaleDateString('ar-SA', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px', textAlign: 'center' }}>
              <thead>
                <tr style={{ backgroundColor: '#f0f0f0' }}>
                  <th style={tableHeaderStyle}>م</th>
                  <th style={tableHeaderStyle}>الكود</th>
                  <th style={tableHeaderStyle}>اسم المشروع</th>
                  <th style={tableHeaderStyle}>العميل</th>
                  <th style={tableHeaderStyle}>تاريخ البداية</th>
                  <th style={tableHeaderStyle}>تاريخ النهاية</th>
                  <th style={tableHeaderStyle}>الحالة</th>
                  <th style={tableHeaderStyle}>قيمة العقد</th>
                </tr>
              </thead>
              <tbody>
                {(projects || []).map((p, i) => (
                  <tr key={p.id}>
                    <td style={tableCellStyle}>{i + 1}</td>
                    <td style={tableCellStyle}>{p.code || '—'}</td>
                    <td style={{ ...tableCellStyle, textAlign: 'right' }}>{p.name_ar || p.name_en || '—'}</td>
                    <td style={{ ...tableCellStyle, textAlign: 'right' }}>{renderCustomerNameText(p)}</td>
                    <td style={tableCellStyle}>{formatDate(p.start_date)}</td>
                    <td style={tableCellStyle}>{formatDate(p.end_date)}</td>
                    <td style={tableCellStyle}>{(STATUS_META[p.status] || {}).label || p.status || '—'}</td>
                    <td style={tableCellStyle}>{formatCurrency(p.contract_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <img
            src={`${publicUrl}/FooterAce.png`}
            alt="Footer"
            style={{ width: '100%', height: 'auto', marginTop: 'auto', borderTop: '2px solid #000', paddingTop: '5px' }}
            crossOrigin="anonymous"
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '7px', marginTop: '3px', position: 'relative', zIndex: 1 }}>
            <span>مكتب البرقاوي مهندسون استشاريون</span>
            <span>{new Date().toLocaleDateString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Projects;
